import React, { Children, cloneElement, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { useDocument } from '@nandorojo/swr-firestore';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';
import { CollectionPath } from '@typings/collections.enum';
import { IEquipmentItem } from '@typings/store/equipment.interface';
import { ISkillItem } from '@typings/store/skill.interface';
import useToggle from '@hooks/useToggle';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import ItemImgCard from '@components/dataDisplay/Store/ItemImgCard';
import useMouse from '@react-hook/mouse-position';
import FollowedDescription from '@components/dataDisplay/board/FollowedDescription';

export const Box = styled(motion.div)<{ $isDraggable?: boolean }>`
  &:hover {
    cursor: ${({ $isDraggable }) =>
      $isDraggable ? 'grab' : 'var(--hoverCursorURL), pointer'};
  }
  &:active {
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grabbing' : undefined)};
  }

  /* DnD 모드: 이미지 네이티브 드래그 / 텍스트 선택 방지 */
  ${({ $isDraggable }) =>
    $isDraggable &&
    `
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;

    & img {
      -webkit-user-drag: none;
      user-drag: none;
      pointer-events: none;
    }
  `}
`;

type Props = {
  originItemID?: string;
  data?: IstoreItem | IEquipmentItem | ISkillItem | IinventoryItem | any;
  collectionPath?: string;
  handleClick?: () => void;
  modal?: any; // modal component
  photocardData?: IPhotoCard;
  followTooltip?: boolean;
  hideReinforce?: boolean;
  // DnD 관련 (GridDroppable이 주입하는 이벤트 핸들러)
  [key: string]: any;
};
function InventoryItem({
  originItemID: originItemIDProp,
  collectionPath: _collectionPathProp, // GridDroppable이 주입하는 inventory path — 무시하고 STORE 고정
  modal,
  data: customData,
  photocardData,
  followTooltip = true,
  hideReinforce = false,
  // GridDroppable이 주입하는 DnD 이벤트 핸들러
  onPointerDown,
  onMouseDown,
  onKeyDown,
  role,
  tabIndex,
  'aria-disabled': ariaDisabled,
  'aria-pressed': ariaPressed,
  'aria-roledescription': ariaRoleDescription,
  'aria-describedby': ariaDescribedBy,
}: Props) {
  // originItemID: 명시적 prop 우선, 없으면 data.originItemID fallback (GridDroppable 패턴)
  const originItemID = originItemIDProp || customData?.originItemID;
  // 스토어 아이템 조회는 항상 STORE 컬렉션 사용
  const _collectionPath = CollectionPath.STORE;
  const [open, handleToggleOpen] = useToggle(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const targetRef = useMouse(ref);
  const [mouseOn, setMouseOn] = useState(false);

  const { data: fetchedOriginData } = originItemID
    ? useDocument<IstoreItem | IEquipmentItem | ISkillItem | any>(
        `${_collectionPath}/${originItemID}`,
        { listen: false },
      )
    : { data: null };
  // 오리지널 item data와 인벤토리 추가속성 data를 합쳐주어야 함
  // customData 값은 undefined 일 수 있음 (? 처리한 이유)
  const mergedMultiple =
    customData?.multiple || (fetchedOriginData as any)?.multiple;

  // originItemID가 있고 실제 원본 데이터도 존재하는 경우
  const _data =
    originItemID && fetchedOriginData
      ? {
          ...customData,
          ...fetchedOriginData,
          id: customData?.id || '',
          isPresent: customData?.isPresent || !!customData?.giftingCharID,
          giftMessage: customData?.giftMessage || '',
          giftingCharID: customData?.giftingCharID || '',
          multiple: mergedMultiple,
        }
      : {
          ...customData,
          title: customData?.customTitle || customData?.title,
          description: customData?.customDescription || customData?.description,
          imgURL: customData?.imgURL || customData?.customImgURL,
          multiple: customData?.multiple,
        };

  if (!_data) return null;

  // GridDroppable이 주입하는 DnD 핸들러 (onPointerDown 등) 존재 시 DnD 모드로 판단
  const isDraggable = !!onPointerDown;

  return (
    <>
      <Box
        ref={ref}
        $isDraggable={isDraggable}
        onClick={handleToggleOpen}
        onMouseEnter={() => setMouseOn(true)}
        onMouseLeave={() => setMouseOn(false)}
        onPointerDown={onPointerDown}
        onMouseDown={onMouseDown}
        onKeyDown={onKeyDown}
        role={role}
        tabIndex={tabIndex}
        aria-disabled={ariaDisabled}
        aria-pressed={ariaPressed}
        aria-roledescription={ariaRoleDescription}
        aria-describedby={ariaDescribedBy}
      >
        <ItemImgCard data={_data} hideReinforce={hideReinforce} />
      </Box>

      {open && (
        <>
          {/* 넘겨받은 modal 을 표시하고, 해당 컴포넌트에 data와 closeModal 을 넘기는 부분 */}
          {Children.map(modal, (child) =>
            cloneElement(child, {
              data: _data,
              originData: _data,
              closeModal: handleToggleOpen,
              photocardData: photocardData,
            }),
          )}
        </>
      )}
      {followTooltip && (
        <FollowedDescription
          targetRef={targetRef}
          data={_data}
          mouseOn={mouseOn && !open}
        />
      )}
    </>
  );
}

export default InventoryItem;
